import React, { useEffect, useState } from "react";
import {
  PhoneCall, Loader2, Plus, Search, Bell, CheckCircle2, X, Phone, Mail,
  Trash2, StickyNote, Users, CalendarClock,
} from "lucide-react";
import {
  crmLeads, crmCreateLead, crmLeadDetail, crmPatchLead, crmDeleteLead,
  crmLogActivity, crmPatchReminder, crmDueReminders,
} from "@/lib/api"; // crmAddReminder also exists for standalone reminders

/* Admin → Lead CRM: the calling desk. Log every call (what was talked,
   outcome), move the lead through the pipeline, set a follow-up reminder —
   reminders email you automatically when due and stay on the strip until done. */

const STATUS_TONE = {
  new: "bg-stone-800 text-stone-300",
  called: "bg-blue-900/50 text-blue-300",
  interested: "bg-amber-900/50 text-amber-300",
  follow_up: "bg-purple-900/50 text-purple-300",
  demo_scheduled: "bg-cyan-900/50 text-cyan-300",
  converted: "bg-emerald-900/50 text-emerald-300",
  lost: "bg-red-900/50 text-red-300",
};
const STATUS_LABEL = {
  new: "New", called: "Called", interested: "Interested", follow_up: "Follow up",
  demo_scheduled: "Demo scheduled", converted: "Converted", lost: "Lost",
};
const when = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

export default function AdminLeadCRM() {
  const [data, setData] = useState(null);
  const [due, setDue] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);   // lead detail {lead, activities, reminders}
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = (status = filter, query = q) => {
    crmLeads(status, query).then(({ data }) => setData(data)).catch(() => setData({ leads: [], counts: {}, statuses: [] }));
    crmDueReminders().then(({ data }) => setDue((data.reminders || []).filter((r) => r.due_at <= data.now))).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openLead = (lead_id) =>
    crmLeadDetail(lead_id).then(({ data }) => setSelected(data)).catch(() => {});

  const markReminder = async (reminder_id, status) => {
    await crmPatchReminder(reminder_id, status).catch(() => {});
    load();
    if (selected) openLead(selected.lead.lead_id);
  };

  return (
    <div data-testid="admin-crm">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-stone-100 flex items-center gap-2"><PhoneCall size={26} className="text-[#C2410C]" /> Lead CRM</h1>
          <p className="text-sm text-stone-400 mt-1 max-w-2xl">Log every call — what was talked, the outcome, the next step. Follow-up reminders email you automatically when due.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="crm-add-lead"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412]">
          <Plus size={15} /> Add lead
        </button>
      </div>

      {/* Due / overdue reminders strip */}
      {due.length > 0 && (
        <div className="mt-6 rounded-2xl border border-amber-700/50 bg-amber-900/15 p-5" data-testid="crm-due-strip">
          <p className="text-xs font-mono uppercase tracking-widest text-amber-300 flex items-center gap-2 mb-3"><Bell size={13} /> {due.length} follow-up{due.length > 1 ? "s" : ""} due</p>
          <div className="space-y-2">
            {due.map((r) => (
              <div key={r.reminder_id} className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl bg-stone-900/60 border border-stone-800 px-4 py-2.5">
                <button onClick={() => openLead(r.lead_id)} className="text-sm font-medium text-stone-100 hover:text-amber-300">
                  {r.lead?.name || "Lead"}{r.lead?.org ? <span className="text-stone-400"> · {r.lead.org}</span> : null}
                </button>
                {r.lead?.phone && <a href={`tel:${r.lead.phone}`} className="text-xs text-emerald-300 inline-flex items-center gap-1 hover:underline"><Phone size={11} /> {r.lead.phone}</a>}
                {r.note && <span className="text-xs text-stone-400">“{r.note}”</span>}
                <span className="text-xs text-stone-500 font-mono">{when(r.due_at)}</span>
                <button onClick={() => markReminder(r.reminder_id, "done")} data-testid={`crm-rem-done-${r.reminder_id}`}
                  className="ml-auto inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-emerald-900/40 text-emerald-300 hover:bg-emerald-900/70">
                  <CheckCircle2 size={12} /> Done
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <button onClick={() => { setFilter(""); load("", q); }}
          className={`px-3 py-1.5 rounded-full text-xs font-medium ${!filter ? "bg-stone-100 text-stone-900" : "bg-stone-800 text-stone-300 hover:bg-stone-700"}`}>
          All {data ? `(${Object.values(data.counts || {}).reduce((a, b) => a + b, 0)})` : ""}
        </button>
        {(data?.statuses || []).map((s) => (
          <button key={s} onClick={() => { setFilter(s); load(s, q); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === s ? "bg-stone-100 text-stone-900" : "bg-stone-800 text-stone-300 hover:bg-stone-700"}`}>
            {STATUS_LABEL[s]} {data?.counts?.[s] ? `(${data.counts[s]})` : ""}
          </button>
        ))}
        <div className="ml-auto relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(filter, q)}
            placeholder="Search name / org / phone" data-testid="crm-search"
            className="pl-8 pr-3 py-2 rounded-full bg-stone-900 border border-stone-700 text-sm text-stone-100 placeholder:text-stone-600 focus:outline-none focus:border-[#C2410C] w-64" />
        </div>
      </div>

      {/* Lead table */}
      <div className="mt-4 rounded-2xl border border-stone-800 bg-[#1c1917] overflow-hidden">
        {!data ? <p className="px-6 py-8 text-sm text-stone-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>
        : data.leads.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Users size={28} className="mx-auto text-stone-700" />
            <p className="mt-3 text-sm text-stone-500">No leads yet. Add the first one — every call you log builds your pipeline.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-stone-300">
              <thead><tr className="border-b border-stone-800 text-[10px] font-mono uppercase tracking-widest text-stone-500">
                {["Lead", "Contact", "Status", "Last activity", "Next follow-up"].map((h) => <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.leads.map((l) => (
                  <tr key={l.lead_id} onClick={() => openLead(l.lead_id)} data-testid={`crm-row-${l.lead_id}`}
                    className="border-b border-stone-800/60 hover:bg-stone-900/50 cursor-pointer">
                    <td className="px-4 py-3">
                      <p className="text-stone-100 font-medium">{l.name}</p>
                      <p className="text-xs text-stone-500">{[l.role, l.org, l.city].filter(Boolean).join(" · ") || l.source}</p>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-stone-400">
                      {l.phone && <p>{l.phone}</p>}
                      {l.email && <p className="truncate max-w-[180px]">{l.email}</p>}
                    </td>
                    <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_TONE[l.status] || STATUS_TONE.new}`}>{STATUS_LABEL[l.status] || l.status}</span></td>
                    <td className="px-4 py-3 text-xs text-stone-500">{when(l.last_activity_at)}</td>
                    <td className="px-4 py-3 text-xs">
                      {l.next_reminder_at
                        ? <span className={`font-mono ${l.next_reminder_at <= new Date().toISOString() ? "text-amber-300" : "text-stone-400"}`}><CalendarClock size={11} className="inline mr-1" />{when(l.next_reminder_at)}</span>
                        : <span className="text-stone-600">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd && <AddLeadModal onClose={() => setShowAdd(false)} onCreated={(lead) => { setShowAdd(false); load(); openLead(lead.lead_id); }} />}
      {selected && (
        <LeadDrawer detail={selected} busy={busy} setBusy={setBusy}
          onClose={() => setSelected(null)}
          onChanged={(d) => { setSelected(d); load(); }}
          onDeleted={() => { setSelected(null); load(); }}
          onReminderDone={markReminder} />
      )}
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 rounded-lg bg-stone-900 border border-stone-700 text-sm text-stone-100 placeholder:text-stone-600 focus:outline-none focus:border-[#C2410C]";

function AddLeadModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", org: "", role: "", phone: "", email: "", city: "", source: "manual", notes: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    if (f.name.trim().length < 2) { setErr("Name is required."); return; }
    setSaving(true); setErr("");
    try { const { data } = await crmCreateLead(f); onCreated(data.lead); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not save."); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-stone-700 bg-[#1c1917] p-6" onClick={(e) => e.stopPropagation()} data-testid="crm-add-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl text-stone-100">Add lead</h2>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-200"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Contact name *" value={f.name} onChange={set("name")} data-testid="crm-f-name" className={inputCls} />
          <input placeholder="Role (TPO, Dean…)" value={f.role} onChange={set("role")} className={inputCls} />
          <input placeholder="College / company" value={f.org} onChange={set("org")} data-testid="crm-f-org" className={`${inputCls} col-span-2`} />
          <input placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="crm-f-phone" className={inputCls} />
          <input placeholder="Email" value={f.email} onChange={set("email")} className={inputCls} />
          <input placeholder="City" value={f.city} onChange={set("city")} className={inputCls} />
          <select value={f.source} onChange={set("source")} className={inputCls}>
            {["manual", "contact", "waitlist", "sales", "linkedin", "referral"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <textarea rows={2} placeholder="Notes (context, who referred, anything)" value={f.notes} onChange={set("notes")} className={`${inputCls} col-span-2 resize-none`} />
        </div>
        {err && <p className="mt-3 text-sm text-red-400">{err}</p>}
        <button onClick={save} disabled={saving} data-testid="crm-f-save"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60">
          {saving && <Loader2 size={14} className="animate-spin" />} Save lead
        </button>
      </div>
    </div>
  );
}

function LeadDrawer({ detail, onClose, onChanged, onDeleted, onReminderDone, busy, setBusy }) {
  const { lead, activities, reminders } = detail;
  const [call, setCall] = useState({ kind: "call", summary: "", outcome: "", status_after: "", remind_at: "", remind_note: "" });
  const [err, setErr] = useState("");
  const set = (k) => (e) => setCall((x) => ({ ...x, [k]: e.target.value }));

  const changeStatus = async (status) => {
    const { data } = await crmPatchLead(lead.lead_id, { status }).catch(() => ({}));
    if (data?.lead) onChanged({ ...detail, lead: data.lead });
  };
  const logIt = async () => {
    if (call.summary.trim().length < 2) { setErr("Write what was talked."); return; }
    setBusy(true); setErr("");
    try {
      const payload = { ...call, status_after: call.status_after || undefined, remind_at: call.remind_at || undefined, remind_note: call.remind_note || undefined, outcome: call.outcome || undefined };
      const { data } = await crmLogActivity(lead.lead_id, payload);
      onChanged({ lead: data.lead, activities: data.activities, reminders: data.reminders });
      setCall({ kind: "call", summary: "", outcome: "", status_after: "", remind_at: "", remind_note: "" });
    } catch (e) { setErr(e?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${lead.name} and their full history?`)) return;
    await crmDeleteLead(lead.lead_id).catch(() => {});
    onDeleted();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-xl h-full overflow-y-auto bg-[#1c1917] border-l border-stone-700 p-6" onClick={(e) => e.stopPropagation()} data-testid="crm-drawer">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl text-stone-100">{lead.name}</h2>
            <p className="text-sm text-stone-400 mt-0.5">{[lead.role, lead.org, lead.city].filter(Boolean).join(" · ")}</p>
            <div className="mt-2 flex flex-wrap gap-3 text-xs">
              {lead.phone && <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1 text-emerald-300 hover:underline"><Phone size={11} /> {lead.phone}</a>}
              {lead.email && <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1 text-blue-300 hover:underline"><Mail size={11} /> {lead.email}</a>}
              <span className="text-stone-500">source: {lead.source}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={del} title="Delete lead" className="p-2 rounded-lg text-stone-500 hover:text-red-400"><Trash2 size={15} /></button>
            <button onClick={onClose} className="p-2 rounded-lg text-stone-500 hover:text-stone-200"><X size={18} /></button>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <span className="text-xs text-stone-500">Status</span>
          <select value={lead.status} onChange={(e) => changeStatus(e.target.value)} data-testid="crm-status-select"
            className="px-3 py-1.5 rounded-full bg-stone-900 border border-stone-700 text-sm text-stone-100 focus:outline-none focus:border-[#C2410C]">
            {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </div>
        {lead.notes && <p className="mt-3 text-sm text-stone-400 bg-stone-900/60 border border-stone-800 rounded-xl px-4 py-3">{lead.notes}</p>}

        {/* Log a call — the golden flow */}
        <div className="mt-6 rounded-2xl border border-stone-700 bg-stone-900/60 p-5" data-testid="crm-log-form">
          <p className="text-xs font-mono uppercase tracking-widest text-[#C2410C] mb-3 flex items-center gap-2"><StickyNote size={13} /> Log a call / note</p>
          <div className="grid grid-cols-2 gap-3">
            <select value={call.kind} onChange={set("kind")} className={inputCls}>
              {["call", "note", "email", "meeting", "whatsapp"].map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <select value={call.status_after} onChange={set("status_after")} className={inputCls} data-testid="crm-log-status">
              <option value="">Keep status</option>
              {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>Move to: {STATUS_LABEL[s]}</option>)}
            </select>
            <textarea rows={3} placeholder="What was talked — points discussed, objections, their questions…" value={call.summary} onChange={set("summary")} data-testid="crm-log-summary" className={`${inputCls} col-span-2 resize-none`} />
            <input placeholder="Outcome (one line, e.g. 'wants pricing PDF, call back Monday')" value={call.outcome} onChange={set("outcome")} className={`${inputCls} col-span-2`} />
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-stone-500">Remind me on</label>
              <input type="datetime-local" value={call.remind_at} onChange={set("remind_at")} data-testid="crm-log-remind" className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-stone-500">Reminder note</label>
              <input placeholder="e.g. send pricing before calling" value={call.remind_note} onChange={set("remind_note")} className={`${inputCls} mt-1`} />
            </div>
          </div>
          {err && <p className="mt-2 text-sm text-red-400">{err}</p>}
          <button onClick={logIt} disabled={busy} data-testid="crm-log-save"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60">
            {busy && <Loader2 size={14} className="animate-spin" />} Save{call.remind_at ? " + set reminder" : ""}
          </button>
        </div>

        {/* Reminders */}
        {reminders?.length > 0 && (
          <div className="mt-6">
            <p className="text-xs font-mono uppercase tracking-widest text-stone-500 mb-2">Reminders</p>
            <div className="space-y-2">
              {reminders.map((r) => (
                <div key={r.reminder_id} className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 text-sm ${r.status === "pending" ? "border-amber-800/50 bg-amber-900/10" : "border-stone-800 bg-stone-900/40 opacity-60"}`}>
                  <Bell size={13} className={r.status === "pending" ? "text-amber-300" : "text-stone-600"} />
                  <span className="font-mono text-xs text-stone-300">{when(r.due_at)}</span>
                  {r.note && <span className="text-xs text-stone-400 flex-1">“{r.note}”</span>}
                  <span className="text-[10px] font-mono uppercase text-stone-500 ml-auto">{r.status}</span>
                  {r.status === "pending" && (
                    <button onClick={() => onReminderDone(r.reminder_id, "done")}
                      className="text-xs px-2.5 py-1 rounded-full bg-emerald-900/40 text-emerald-300 hover:bg-emerald-900/70">Done</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="mt-6">
          <p className="text-xs font-mono uppercase tracking-widest text-stone-500 mb-2">History</p>
          {(!activities || activities.length === 0) ? <p className="text-sm text-stone-600">No activity yet — log your first call above.</p> : (
            <div className="space-y-3">
              {activities.map((a) => (
                <div key={a.activity_id} className="rounded-xl border border-stone-800 bg-stone-900/40 px-4 py-3">
                  <div className="flex items-center gap-2 text-[11px] font-mono text-stone-500">
                    <span className="uppercase text-stone-400">{a.kind}</span>
                    <span>{when(a.created_at)}</span>
                    {a.status_after && <span className={`px-2 py-0.5 rounded-full ${STATUS_TONE[a.status_after] || ""}`}>→ {STATUS_LABEL[a.status_after] || a.status_after}</span>}
                    {a.by && <span className="ml-auto truncate max-w-[160px]">{a.by}</span>}
                  </div>
                  <p className="text-sm text-stone-200 mt-1.5 whitespace-pre-wrap">{a.summary}</p>
                  {a.outcome && <p className="text-xs text-amber-300/90 mt-1">Outcome: {a.outcome}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
